import { http } from "../utils/http";
import { API_ENDPOINTS } from "../config";
import { store } from "../State/store/configureStore";
import { showToaster } from "../State/actions/toaster";
import { ROLE_PATIENT } from "../security/roles";
import globalCacheService from "./globalCacheService";

class ContentLibraryService {
  static load(page = 0, filter, skipCache = false) {
    let {
      searchTerm,
      pTypeId,
      topicId,
      status,
      allStatus,
      onlyFeaturedContent,
      sortBy = "lastUpdatedOn",
      sortDir = "DESC",
      onlyPublicContent,
      size = 20
    } = filter;

    let url = API_ENDPOINTS.loadContentLibrary() + `?page=${page}&size=${size}`;

    if (searchTerm) {
      url += "&searchTerm=" + searchTerm;
    }
    if (pTypeId) {
      url += "&pTypeId=" + pTypeId;
    }
    if (topicId) {
      url += "&topicId=" + topicId;
    }
    if (status) {
      url += "&status=" + status;
    }

    if (allStatus) {
      url += "&allStatus=True";
    }
    if (onlyFeaturedContent) {
      url += "&onlyFeaturedContent=true";
    }
    if (onlyPublicContent) {
      url += "&onlyPublicContent=true";
    }
    if (sortBy) {
      url += "&sortBy=" + sortBy;
    }
    if (sortDir) {
      url += "&sortDir=" + sortDir;
    }

    // Create cache key based on page and filter params
    const filterKeys = Object.keys(filter || {}).sort();
    const filterString = filterKeys.map(key => `${key}:${filter[key]}`).join('|');
    const cacheKey = `contentLibrary:page:${page}:${filterString}`;

    if (skipCache) {
      globalCacheService.clearCache(cacheKey);
    }

    // Only cache page 0 requests to avoid complex pagination cache management
    if (page === 0) {
      return globalCacheService.cacheApiCall(
        cacheKey,
        () => http.request(url).then(data => {
          let list = data && data._embedded ? data._embedded.libraries : [];
          return {
            number: data.page.number,
            totalPages: data.page.totalPages,
            loadMore: page + 1 < data.page.totalPages,
            totalItems: data && data.page ? data.page.totalElements : 0,
            list: list
          };
        }),
        2 * 60 * 1000 // 2 minutes cache for content list
      );
    }

    // For pagination beyond page 0, don't cache
    return http.request(url).then(data => {
      let list = data && data._embedded ? data._embedded.libraries : [];
      return {
        number: data.page.number,
        totalPages: data.page.totalPages,
        loadMore: page + 1 < data.page.totalPages,
        totalItems: data && data.page ? data.page.totalElements : 0,
        list: list
      };
    });
  }

  static add(data, options = {}) {
    return http
      .request(API_ENDPOINTS.addLibraryContent(), "post", data)
      .then(data => {
        if (!options.silent) {
          store.dispatch(showToaster("Library has been added.", "success", 5));
        }
        return data;
      });
  }

  static loadTopics(patientTypeId, skipCache = false) {
    let url = API_ENDPOINTS.loadTopics() + `?page=0&size=1000`;
    if (patientTypeId) {
      url += "&pTypeId=" + patientTypeId;
    }

    const cacheKey = `contentLibraryTopics:${patientTypeId || 'all'}`;
    
    if (skipCache) {
      globalCacheService.clearCache(cacheKey);
    }

    return globalCacheService.cacheApiCall(
      cacheKey,
      () => http.request(url).then(data => {
        return data && data._embedded ? data._embedded.libraryTopics : [];
      }),
      10 * 60 * 1000 // 10 minutes cache
    );
  }

  static loadArticle(articleId) {
    let role = store.getState().user.role;
    let url = null;
    if (role === ROLE_PATIENT) {
      url = API_ENDPOINTS.loadArticlePatient(articleId);
    } else {
      url = API_ENDPOINTS.loadArticle(articleId);
    }
    return http.request(url);
  }

  static loadById(libraryId) {
    return http.request(API_ENDPOINTS.loadArticle(libraryId));
  }

  static edit(contentId, data) {
    return http
      .request(API_ENDPOINTS.loadArticle(contentId), "patch", data)
      .then(result => {
        store.dispatch(showToaster("Library has been updated.", "success", 5));
      });
  }

  static addTopic(data) {
    return http.request(API_ENDPOINTS.addLibraryTopic(), "post", data);
  }

  static loadTopic(topicId) {
    return http.request(API_ENDPOINTS.loadTopic(topicId)).then(data => {
      return data._embedded ? data._embedded.libraryTopics : [];
    });
  }

  static editTopic(topicId, data) {
    return http.request(API_ENDPOINTS.loadTopic(topicId), "patch", data);
  }

  static deleteTopic(topicId) {
    return http.request(API_ENDPOINTS.loadTopic(topicId), "delete");
  }

  static deleteArticle(articleId) {
    return http.request(API_ENDPOINTS.loadArticle(articleId), "delete");
  }

  static loadLibraryItemForSignup(libId, locale) {
    return http.request(API_ENDPOINTS.loadLibForSignUp(libId, locale));
  }

  static exportLibraryItem(libId) {
    return http.request(API_ENDPOINTS.libraryExport(libId));
  }

  static cloneALibrary(libId, data) {
    return http.request(API_ENDPOINTS.cloneLibrary(libId), "post", data);
  }

  static prepareAudioScript(libraryId) {
    return http.request(API_ENDPOINTS.prepareLibraryAudioScript(libraryId), "post");
  }

  static generateAudioFromScript(libraryId, script) {
    return http.request(
      API_ENDPOINTS.generateLibraryAudioFromScript(libraryId),
      "post",
      { script }
    );
  }

  static getAudioJobStatus(libraryId, pendingProcessId) {
    return http.request(
      API_ENDPOINTS.getLibraryAudioJobStatus(libraryId, pendingProcessId)
    );
  }

  static generateTranslations(patientTypeId) {
    return http.request(
      API_ENDPOINTS.generateLibraryTranslations(patientTypeId),
      "post"
    );
  }

  static loadLibraryByName(name, patientId) {
    let url = API_ENDPOINTS.loadContentLibraryByName(encodeURIComponent(name));
    if (patientId) {
      url += "&patientId=" + patientId;
    }
    return http.request(url).then(response => {
      return response;
    });
  }

  static getIndexStatusAll() {
    return http.request(API_ENDPOINTS.contentIndexStatusAll());
  }

  static getIndexStatus(patientTypeId) {
    return http.request(API_ENDPOINTS.contentIndexStatus(patientTypeId));
  }

  static reindexPatientType(patientTypeId) {
    return http.request(API_ENDPOINTS.contentIndexReindex(patientTypeId), "post");
  }

  static reindexFacility() {
    return http.request(API_ENDPOINTS.contentIndexReindexFacility(), "post");
  }
}

const libraryService = ContentLibraryService;

export default libraryService;
